import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    Shield,
    Package,
    Truck,
    Home,
    Building2,
    Heart,
    Target,
    User,
    ChevronDown,
    ChevronUp,
    Phone,
    Radio
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roleConfig = {
    chef_dispositif: { label: 'Chef de Dispositif', icon: Shield, bg: 'bg-red-500', bgLight: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/40' },
    logistique: { label: 'Logistique', icon: Package, bg: 'bg-amber-500', bgLight: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/40' },
    moyen: { label: 'Moyens', icon: Truck, bg: 'bg-blue-500', bgLight: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/40' },
    gymnase_chef_poste: { label: 'Chef de Poste CARE', icon: Home, bg: 'bg-cyan-500', bgLight: 'bg-cyan-500/15', text: 'text-cyan-400', border: 'border-cyan-500/40' },
    gymnase_admin: { label: 'Admin CARE', icon: Heart, bg: 'bg-purple-500', bgLight: 'bg-purple-500/15', text: 'text-purple-400', border: 'border-purple-500/40' },
    secouriste: { label: 'Secouriste', icon: Heart, bg: 'bg-rose-500', bgLight: 'bg-rose-500/15', text: 'text-rose-400', border: 'border-rose-500/40' },
    equipier: { label: 'Equipier', icon: Heart, bg: 'bg-orange-500', bgLight: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/40' },
    commune: { label: 'Service Communal', icon: Building2, bg: 'bg-green-500', bgLight: 'bg-green-500/15', text: 'text-green-400', border: 'border-green-500/40' },
    agent_communaux: { label: 'Agent communaux', icon: Building2, bg: 'bg-lime-500', bgLight: 'bg-lime-500/15', text: 'text-lime-400', border: 'border-lime-500/40' },
    reserve_securite_civile: { label: 'Reserve securite civile', icon: Shield, bg: 'bg-sky-500', bgLight: 'bg-sky-500/15', text: 'text-sky-400', border: 'border-sky-500/40' },
    entreprise_exterieur: { label: 'Entreprise exterieur', icon: Truck, bg: 'bg-teal-500', bgLight: 'bg-teal-500/15', text: 'text-teal-400', border: 'border-teal-500/40' },
    entreprise_exterieur_contrat: { label: 'Entreprise ext. sous contrat', icon: Truck, bg: 'bg-indigo-500', bgLight: 'bg-indigo-500/15', text: 'text-indigo-400', border: 'border-indigo-500/40' },
};

const VIEWS = {
    generale: { label: 'Generale', roles: ['chef_dispositif', 'logistique', 'moyen', 'gymnase_chef_poste', 'gymnase_admin', 'commune', 'equipier', 'secouriste', 'agent_communaux', 'reserve_securite_civile', 'entreprise_exterieur', 'entreprise_exterieur_contrat'] },
    secours: { label: 'Vision Secours', roles: ['chef_dispositif', 'logistique', 'moyen', 'gymnase_chef_poste', 'gymnase_admin', 'equipier', 'secouriste', 'agent_communaux', 'reserve_securite_civile', 'entreprise_exterieur', 'entreprise_exterieur_contrat'] },
    commune: { label: 'Dashboard Commune', roles: ['chef_dispositif', 'commune'] }
};

const OrgNode = ({ roleKey, users, centers, isRoot }) => {
    const config = roleConfig[roleKey] || roleConfig.commune;
    const Icon = config.icon;
    const [expanded, setExpanded] = useState(isRoot || users.length <= 3);

    return (
        <div className="flex flex-col items-center" data-testid={`org-role-${roleKey}`}>
            {!isRoot && <div className="w-px h-6 bg-zinc-600" />}
            <button
                onClick={() => setExpanded(!expanded)}
                className={`relative px-5 py-3 rounded-xl ${config.bgLight} border ${config.border} hover:brightness-110 transition-all min-w-[200px]`}
            >
                <div className="flex items-center gap-2 justify-center">
                    <div className={`w-7 h-7 rounded-lg ${config.bg} flex items-center justify-center`}>
                        <Icon className="w-4 h-4 text-white" />
                    </div>
                    <span className={`font-semibold text-sm ${config.text}`}>{config.label}</span>
                    <Badge className={`${config.bgLight} ${config.text} border-0 text-xs ml-1`}>{users.length}</Badge>
                </div>
                {users.length > 0 && (
                    <div className="absolute -bottom-1 right-2">
                        {expanded ? <ChevronUp className="w-3 h-3 text-zinc-500" /> : <ChevronDown className="w-3 h-3 text-zinc-500" />}
                    </div>
                )}
            </button>

            {expanded && users.length > 0 && (
                <>
                    <div className="w-px h-3 bg-zinc-700" />
                    <div className="space-y-1 min-w-[200px]">
                        {users.map(u => {
                            const center = centers?.find(c => c.id === u.assigned_center_id);
                            return (
                                <div key={u.id} className="px-3 py-2 rounded-lg bg-zinc-800/60 border border-zinc-700/50" data-testid={`org-user-${u.id}`}>
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center flex-shrink-0">
                                            <User className="w-3 h-3 text-zinc-400" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-white truncate">{u.name}</p>
                                            {u.position && <p className="text-[10px] text-zinc-400 truncate">{u.position}</p>}
                                        </div>
                                    </div>
                                    {/* Contact info */}
                                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 ml-8">
                                        {u.phone && (
                                            <span className="text-[10px] text-cyan-400 flex items-center gap-0.5" data-testid={`org-phone-${u.id}`}>
                                                <Phone className="w-2.5 h-2.5" />{u.phone}
                                            </span>
                                        )}
                                        {u.canal_radio && (
                                            <span className="text-[10px] text-amber-400 flex items-center gap-0.5" data-testid={`org-radio-${u.id}`}>
                                                <Radio className="w-2.5 h-2.5" />{u.canal_radio}
                                            </span>
                                        )}
                                        {center && (
                                            <span className="text-[10px] text-zinc-500 flex items-center gap-0.5">
                                                <Home className="w-2.5 h-2.5" />{center.name}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

const OrganigramPage = () => {
    const { token } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeView, setActiveView] = useState('generale');

    useEffect(() => {
        const fetchOrg = async () => {
            try {
                const res = await axios.get(`${API}/organigramme`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setData(res.data);
            } catch (error) {
                console.error('Failed to fetch organigramme:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchOrg();
    }, [token]);

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" /></div>;
    if (!data) return <div className="text-center text-zinc-500 py-12">Aucune donnee disponible</div>;

    const viewConfig = VIEWS[activeView];
    const visibleRoles = viewConfig.roles;

    const hasRole = (role) => visibleRoles.includes(role) && (data[role]?.length || 0) > 0;

    // Secours roles for branch grouping
    const hasChefPoste = hasRole('gymnase_chef_poste');
    const hasGymAdmin = hasRole('gymnase_admin');
    const hasEquipier = hasRole('equipier');
    const hasSecouriste = hasRole('secouriste');

    const level1Roles = visibleRoles.filter(r => !['chef_dispositif', 'gymnase_admin', 'secouriste'].includes(r) && hasRole(r));
    // Add gymnase_admin and secouriste as nested
    const childBranches = level1Roles.length + (hasSecouriste && !hasEquipier ? 1 : 0) + (hasGymAdmin && !hasChefPoste ? 1 : 0);

    return (
        <div className="space-y-6" data-testid="organigramme-page">
            <div className="page-header">
                <h1 className="text-3xl font-bold text-white">Organigramme</h1>
                <p className="page-subtitle">Structure organisationnelle de crise</p>
            </div>

            {/* View Tabs */}
            <div className="flex gap-2" data-testid="organigramme-tabs">
                {Object.entries(VIEWS).map(([key, v]) => (
                    <button
                        key={key}
                        onClick={() => setActiveView(key)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeView === key
                            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40'
                            : 'bg-zinc-800/50 text-zinc-400 border border-zinc-700/50 hover:bg-zinc-800'
                        }`}
                        data-testid={`tab-${key}`}
                    >
                        {v.label}
                    </button>
                ))}
            </div>

            {/* Tree */}
            <Card className="content-card overflow-x-auto">
                <CardContent className="py-8 px-4">
                    <div className="flex flex-col items-center min-w-[800px]">
                        {/* Level 0: Chef de Dispositif */}
                        {hasRole('chef_dispositif') && (
                            <OrgNode roleKey="chef_dispositif" users={data.chef_dispositif || []} centers={data.centers || []} isRoot />
                        )}

                        {childBranches > 0 && hasRole('chef_dispositif') && (
                            <>
                                <div className="w-px h-6 bg-zinc-600" />
                                <div className="relative w-full flex justify-center">
                                    <div className="absolute top-0 h-px bg-zinc-600" style={{
                                        width: childBranches > 1 ? `${Math.min(90, childBranches * 22)}%` : '0px'
                                    }} />
                                </div>
                            </>
                        )}

                        {/* Level 1 */}
                        <div className="flex items-start justify-center gap-8 flex-wrap mt-0">
                            {hasRole('logistique') && (
                                <OrgNode roleKey="logistique" users={data.logistique} centers={data.centers || []} />
                            )}
                            {hasRole('moyen') && (
                                <OrgNode roleKey="moyen" users={data.moyen} centers={data.centers || []} />
                            )}
                            {hasChefPoste && (
                                <div className="flex flex-col items-center">
                                    <OrgNode roleKey="gymnase_chef_poste" users={data.gymnase_chef_poste} centers={data.centers || []} />
                                    {hasGymAdmin && (
                                        <OrgNode roleKey="gymnase_admin" users={data.gymnase_admin} centers={data.centers || []} />
                                    )}
                                </div>
                            )}
                            {!hasChefPoste && hasGymAdmin && (
                                <OrgNode roleKey="gymnase_admin" users={data.gymnase_admin} centers={data.centers || []} />
                            )}
                            {(hasEquipier || hasSecouriste) && (
                                <div className="flex flex-col items-center">
                                    {hasEquipier && (
                                        <OrgNode roleKey="equipier" users={data.equipier} centers={data.centers || []} />
                                    )}
                                    {hasSecouriste && (
                                        <OrgNode roleKey="secouriste" users={data.secouriste} centers={data.centers || []} />
                                    )}
                                </div>
                            )}
                            {hasRole('commune') && (
                                <OrgNode roleKey="commune" users={data.commune} centers={data.centers || []} />
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default OrganigramPage;
